// Copyright (c) 2025 TrustCloud Corporation - All rights reserved.
//
// This software contains valuable confidential and proprietary information of
// TrustCloud Corporation
//
// Unauthorized use, reproduction, transmission, or distribution of this file and its
// contents is a violation of applicable law.

import React, { useMemo, useState } from 'react';
import { Card, IconButton, TabList, TabTrigger, Tag, Typography } from '@kintent/glide';
import { UilExternalLinkAlt as ExternalLinkIcon } from '@iconscout/react-unicons';
import { Link, useCurrentRoute } from 'react-navi';
import { createColumnHelper } from '@tanstack/react-table';

import * as Styles from './EnterpriseCertificationListv2.styles';
import CustomTag from '../../../../../components/CustomTag/CustomTag';
import { Helmet } from '../../../../../components/Helmet';
import { Flex } from '../../../../../components/Flex';
import ComplianceStandardLogoContainer from '../../components/ComplianceStandardLogoContainer';
import { withTrustShareNavigationBar } from '../../../../../components/NavigationBar';
import {
  getDefaultCertificationDescription,
  getTeamCertificationsWithChildren,
  sortSections,
} from '../../../../../lib/utils';
import {
  useAuthService,
  useControlList,
  useFrameworkList,
  useFrameworkSectionList,
  useProgramContent,
  useTeamCertificationList,
} from '../../../../../lib/state';
import {
  CERTIFICATION_STATUS,
  CERTIFICATION_STATUS_DISPLAY_NAME_MAP,
  CONTROL_STATES,
  EXCLUDED_STANDARDS_FROM_COMPLIANCE_MAPPING,
  getCertificationCardTitle,
  getCertificationLogo,
  getCertificationLogoSrcSet,
} from '../../../../../lib/constants';
import getDescription from '../../lib/getDescription';

// Constants
const PAGE_TITLE = 'Certifications';
const ALL_CERTIFICATIONS = 'All';

const SHOW_ALL = 'All';
const SHOW_PUBLIC = 'Public';
const SHOW_PRIVATE = 'Private';

const REPORTS_ACCESS_LEVELS = {
  [SHOW_ALL]: 'all',
  [SHOW_PUBLIC]: 'public',
  [SHOW_PRIVATE]: 'private',
};

const STATUS_COLOR_MAP = {
  [CERTIFICATION_STATUS.IN_PROGRESS]: 'notice',
  [CERTIFICATION_STATUS.COMPLETE]: 'positive',
};

// Table config
const HELPER = createColumnHelper();

const REPORTS_TABLE_COLUMNS = [
  HELPER.accessor('filename', {
    cell: (props) => {
      const value = props.getValue();
      return value ?? '--';
    },
    header: 'Document Name',
    id: 'filename',
  }),
  HELPER.accessor('description', {
    cell: (props) => {
      const value = props.getValue();
      return value ?? '--';
    },
    header: 'Description',
    id: 'description',
  }),
  HELPER.accessor('title', {
    cell: (props) => {
      const value = props.getValue();
      return value ?? '--';
    },
    header: 'Product',
    id: 'product',
  }),
  HELPER.accessor('_metadata.lastModifiedDate', {
    cell: (props) => {
      const date = new Date(props.getValue());
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
      });
    },
    header: 'Last Updated',
    id: 'last_updated',
  }),
  HELPER.accessor('link', {
    cell: (props) => {
      const { certificationUrl } = props.row.original;

      if (!certificationUrl) return null;

      const handleRequestAccess = () => {
        window.open(certificationUrl, '_blank');
      };

      return (
        <IconButton
          onClick={handleRequestAccess}
          icon={<ExternalLinkIcon size={16} />}
        />
      );
    },
    header: '',
    id: 'link',
  }),
];

// Sorts the team certifications by their short name
function sortTeamCertifications(certifications) {
  return certifications?.sort((a, b) => {
    if (a.certification.shortName < b.certification.shortName) return -1;
    if (a.certification.shortName > b.certification.shortName) return 1;
    return 0;
  });
}

function getProductsForTeamCertification(teamCertification) {
  const { children } = teamCertification;
  return children
    ?.map((child) => child.title?.trim())
    .sort()
    .join(', ');
}

function groupCertificationsByProducts(teamCertifications) {
  return (
    teamCertifications?.reduce((acc, teamCertification) => {
      const { certification, children, subtype } = teamCertification;
      const key = subtype ? `${certification.shortName}${subtype}` : certification.shortName;

      if (!acc.has(key)) {
        acc.set(key, []);
      }

      children?.forEach((child) => {
        if (child.title) {
          acc.get(key).push(child.title.trim());
        }
      });

      return acc;
    }, new Map()) ?? new Map()
  );
}

const filterCertificationsByProduct = (teamCertifications, selectedProduct) => {
  return teamCertifications?.filter((teamCertification) => {
    const { children } = teamCertification;
    const products = children?.map((child) => child.title?.trim()).join(',');
    return products?.includes(selectedProduct);
  });
};

const filterChildrenByProduct = (certification, selectedProduct) => {
  if (selectedProduct === ALL_CERTIFICATIONS) {
    return certification;
  }
  // Filter children based on the selected product
  return certification?.children?.find((child) => {
    const products = child.title?.trim();
    return products?.includes(selectedProduct);
  });
};

function LeftPanel({ teamCertifications }) {
  const { data } = useCurrentRoute();

  // If the slug does not exist, we're on the list page
  const isListView = data.slug == null;

  return (
    <Styles.LeftPanel data-testid="certification-section-left-panel">
      <div>
        <Typography
          as={Link}
          level="8"
          className={isListView ? 'active' : null}
          href="/certifications"
        >
          All
        </Typography>
      </div>
      <ul>
        {teamCertifications?.map((teamCertification) => {
          return (
            <LeftPanelListItem
              key={teamCertification.id}
              teamCertification={teamCertification}
            />
          );
        })}
      </ul>
    </Styles.LeftPanel>
  );
}

function LeftPanelListItem(props) {
  const { data } = useCurrentRoute();
  const { teamCertification } = props;
  const { certification } = teamCertification;

  // Use the slug in the url to determine the active list item
  const certificationSlug = `${certification.shortName}${teamCertification.subtype || ''}`;
  const shouldApplyActiveClassName = data.slug === certificationSlug;

  const title = getCertificationCardTitle(certification.shortName, teamCertification.subtype);
  const detailHref = `/certifications/${encodeURIComponent(
    `${certification.shortName}${teamCertification.subtype || ''}`
  )}`;

  return (
    <li>
      <Link
        href={detailHref}
        className={shouldApplyActiveClassName ? 'active' : null}
      >
        <ComplianceStandardLogoContainer>
          <img
            src={getCertificationLogo(certification.shortName, teamCertification.subtype)}
            srcSet={getCertificationLogoSrcSet(certification.shortName, teamCertification.subtype)}
            alt="Certification Logo"
          />
        </ComplianceStandardLogoContainer>
        <Typography
          level="8"
          strength="100%"
        >
          {title}
        </Typography>
      </Link>
    </li>
  );
}

function CertificationContentPanel({ teamCertifications }) {
  const { data } = useCurrentRoute();
  const [filterValue, setFilterValue] = useState(ALL_CERTIFICATIONS);

  const certificationList = useMemo(() => {
    if (filterValue === ALL_CERTIFICATIONS) {
      return teamCertifications;
    }
    return filterCertificationsByProduct(teamCertifications, filterValue);
  }, [filterValue, teamCertifications]);

  const selectedCertification = certificationList.find((item) => {
    return `${item.certification.shortName}${item.subtype || ''}` === data.slug;
  });

  const productMap = useMemo(() => {
    if (data.slug) {
      return groupCertificationsByProducts([selectedCertification]);
    }
    return groupCertificationsByProducts(teamCertifications);
  }, [certificationList, data.slug, teamCertifications]);

  const dedupedProducts = [
    ALL_CERTIFICATIONS,
    ...new Set(
      Array.from(productMap.values())
        .flat()
        .sort((a, b) => {
          if (a < b) return -1;
          if (a > b) return 1;
          return 0;
        })
    ),
  ];

  const handleFilterChange = (product) => setFilterValue(product);

  return (
    <Styles.CertificationContentPanel>
      <div className="certification-content-product-filter-container">
        <Typography>Filter by Product</Typography>
        <Styles.TabListContainer
          defaultValue={ALL_CERTIFICATIONS}
          variant="content-switcher"
          className="product-filter-tab-root"
        >
          <TabList>
            {dedupedProducts.map((product) => {
              return (
                <TabTrigger
                  key={product}
                  value={product}
                  onClick={() => handleFilterChange(product)}
                >
                  {product}
                </TabTrigger>
              );
            })}
          </TabList>
        </Styles.TabListContainer>
      </div>
      {data.slug == null ? (
        <Styles.CertificationGrid>
          {certificationList?.map((teamCertification) => {
            return (
              <CertificationGridItem
                teamCertification={teamCertification}
                key={teamCertification.id}
              />
            );
          })}
        </Styles.CertificationGrid>
      ) : (
        <Styles.CertificationDetailGrid>
          <CertificationDetailHeading
            selectedCertification={selectedCertification}
            filterValue={filterValue}
          />
          <CertificationMappingSection teamCertifications={teamCertifications} />
          <CertificationReportsSection teamCertifications={teamCertifications} />
        </Styles.CertificationDetailGrid>
      )}
    </Styles.CertificationContentPanel>
  );
}

function CertificationDetailStatuses({ selectedCertification }) {
  return (
    <Flex gap="42px">
      <Flex
        gap="8px"
        direction="column"
        alignItems="flex-start"
      >
        <Typography level="9">Certification status</Typography>
        <Tag
          variant={STATUS_COLOR_MAP[selectedCertification.status]}
          type="system"
        >
          {CERTIFICATION_STATUS_DISPLAY_NAME_MAP[selectedCertification.status]}
        </Tag>
      </Flex>
      {selectedCertification.certificationDate && (
        <Flex
          gap="8px"
          direction="column"
          alignItems="flex-start"
        >
          <Typography level="9">Date achieved</Typography>
          <Typography>
            {new Date(selectedCertification.certificationDate).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: '2-digit',
            })}
          </Typography>
        </Flex>
      )}
      {selectedCertification.auditFirmWebsite && selectedCertification.auditNameFirm && (
        <Flex
          gap="8px"
          direction="column"
          alignItems="flex-start"
        >
          <Typography level="9">Name of audit firm</Typography>
          <Link href={selectedCertification.auditFirmWebsite}>
            <Typography>{selectedCertification.auditNameFirm}</Typography>
          </Link>
        </Flex>
      )}
    </Flex>
  );
}

function CertificationDetailHeading({ selectedCertification, filterValue }) {
  const { currentTeam } = useAuthService();
  const [programContent] = useProgramContent();

  const description = getDescription({
    selectedCertification,
    currentTeam,
    selectedDocument: null,
    programContent,
  });

  const filteredCertification = filterChildrenByProduct(selectedCertification, filterValue);
  const title = getCertificationCardTitle(filteredCertification.certification.shortName, filteredCertification.subtype);

  return (
    <Card className="certification-detail-heading-card">
      <Flex gap="16px">
        <div className="certification-detail-heading-card-logo-container">
          <img
            src={getCertificationLogo(filteredCertification.certification.shortName, filteredCertification.subtype)}
            srcSet={getCertificationLogoSrcSet(
              filteredCertification.certification.shortName,
              filteredCertification.subtype
            )}
            alt="Certification Logo"
          />
        </div>
        <Typography
          as="h3"
          level="3"
          strength="100%"
          className="certification-detail-heading-card-title"
        >
          {title}
        </Typography>
      </Flex>
      <Flex
        gap="24px"
        direction="column"
      >
        <Typography
          as="p"
          level="7"
          strength="60%"
          className="certification-detail-heading-card-description"
          dangerouslySetInnerHTML={{ __html: filteredCertification.description ?? description }}
        />
        {filterValue !== ALL_CERTIFICATIONS && filteredCertification && (
          <CertificationDetailStatuses selectedCertification={filteredCertification} />
        )}
      </Flex>
    </Card>
  );
}

function CertificationMappingSection({ teamCertifications }) {
  const { data } = useCurrentRoute();
  const { data: frameworks } = useFrameworkList();

  const selectedCertification = teamCertifications.find((item) => {
    return `${item.certification.shortName}${item.subtype || ''}` === data.slug;
  });

  // Return early if the selected certification is not found
  if (selectedCertification == null) return null;

  const title = getCertificationCardTitle(selectedCertification.certification.shortName, selectedCertification.subtype);
  const mappingTitle = `${title} Control, Policy mapping`;

  // Filter catalog frameworks
  const catalogFrameworks = useMemo(() => {
    return frameworks?.filter((framework) => framework.isCatalog);
  }, [frameworks]);

  const framework = catalogFrameworks.find((item) => {
    return item.id === selectedCertification.certification.id;
  });

  // Return early if the framework is not found
  if (framework == null) return null;

  const { data: frameworkSections } = useFrameworkSectionList(framework.id);

  // Return early if the frameworkSections is not an array
  if (frameworkSections == null || Array.isArray(frameworkSections) === false) return null;

  // Return if the framework is in the EXCLUDED_STANDARDS_FROM_COMPLIANCE_MAPPING list
  if (EXCLUDED_STANDARDS_FROM_COMPLIANCE_MAPPING.includes(framework.shortName)) return null;

  const sortedFrameworkSections = (frameworkSections ?? [])?.sort((a, b) => sortSections(a, b, framework));
  if (sortedFrameworkSections.length === 0) return null;

  return (
    <Card className="certification-detail-mapping-card">
      <Typography
        as="h3"
        level="3"
        strength="100%"
        className="certification-detail-mapping-card-title"
      >
        {mappingTitle}
      </Typography>
      <Typography
        as="p"
        level="8"
        strength="80%"
      >
        We have provided our ISO Control and Policy mappings and status for your visibility
      </Typography>
      {sortedFrameworkSections.map((section) => {
        return (
          <CertificationMappingRow
            key={section.id}
            framework={framework}
            section={section}
          />
        );
      })}
    </Card>
  );
}

function CertificationMappingRow({ framework, section }) {
  const [controls] = useControlList();
  const policyCount = section.programPolicyMapping?.length;

  const subsectionReferenceIds = section.subsections.map(({ referenceId }) => referenceId);

  const controlCountInSection = useMemo(
    () =>
      controls.filter((control) => {
        const controlMappingToThisFramework = control.complianceMapping?.mappings[framework.shortName];
        return (
          controlMappingToThisFramework?.controls.find(({ controlId }) => subsectionReferenceIds.includes(controlId)) &&
          control.state.value === CONTROL_STATES.ADOPTED
        );
      }).length,
    [controls, subsectionReferenceIds, framework.shortName]
  );

  return (
    <Styles.MappingRow>
      <Typography
        as="p"
        level="8"
      >
        {section.displayIdentifier && (
          <Tag className="certification-mapping-row-display-identifier">{section.displayIdentifier}</Tag>
        )}
        <Typography
          level="8"
          strength="100%"
        >
          {section.title}
        </Typography>
      </Typography>
      <Typography
        level="8"
        strength="100%"
      >
        {controlCountInSection > 0 ? `Controls: ${controlCountInSection}` : ''}
      </Typography>
      <Typography
        level="8"
        strength="100%"
      >
        {policyCount > 0 ? `Policies: ${policyCount}` : ''}
      </Typography>
      {controlCountInSection > 0 && policyCount > 0 ? (
        <Tag
          type="system"
          variant="positive"
          className="mapping-tag"
        >
          Fully Mapped
        </Tag>
      ) : (
        <div />
      )}
    </Styles.MappingRow>
  );
}

const CertificationReportsSection = ({ teamCertifications }) => {
  const { data } = useCurrentRoute();
  const [viewType, setViewType] = useState(SHOW_ALL);

  const selectedCertification = useMemo(() => {
    return teamCertifications.find((item) => {
      return `${item.certification.shortName}${item.subtype || ''}` === data.slug;
    });
  }, [teamCertifications, data.slug]);

  const filteredCertifications = useMemo(() => {
    if (viewType === SHOW_ALL) {
      return selectedCertification.children;
    }

    return selectedCertification.children.filter(
      (child) => child.trustShareAccessLevel === REPORTS_ACCESS_LEVELS[viewType]
    );
  }, [viewType, selectedCertification]);

  const handleFilterReports = (value) => {
    setViewType(value);
  };

  // Return early if the selected certification is not found
  if (selectedCertification == null) return null;
  if (selectedCertification.children.length === 0) return null;

  return (
    <Styles.ReportsContainer>
      <Styles.ReportsHeaderContainer>
        <Styles.ReportsTitleWrapper>
          <CustomTag>{selectedCertification.children.length ?? 0}</CustomTag>
          <Typography
            as="h3"
            level="3"
            strength="100%"
            className="certification-detail-mapping-card-title"
          >
            Reports
          </Typography>
        </Styles.ReportsTitleWrapper>
        <Styles.TabListContainer
          defaultValue="All"
          variant="content-switcher"
        >
          <TabList>
            <TabTrigger
              value={SHOW_ALL}
              onClick={() => handleFilterReports(SHOW_ALL)}
            >
              All
            </TabTrigger>
            <TabTrigger
              value={SHOW_PUBLIC}
              onClick={() => handleFilterReports(SHOW_PUBLIC)}
            >
              Public
            </TabTrigger>
            <TabTrigger
              value={SHOW_PRIVATE}
              onClick={() => handleFilterReports(SHOW_PRIVATE)}
            >
              Private
            </TabTrigger>
          </TabList>
        </Styles.TabListContainer>
      </Styles.ReportsHeaderContainer>
      <Styles.ReportsTable
        data={filteredCertifications}
        columns={REPORTS_TABLE_COLUMNS}
        getRowId={(row) => row.id}
        pagination={false}
        sorting
        search
        filtering={false}
        columnSelection
        shouldHideTitleBar
      />
    </Styles.ReportsContainer>
  );
};

function CertificationGridItem({ teamCertification }) {
  const { currentTeam } = useAuthService();
  const { certification } = teamCertification;

  const products = useMemo(() => getProductsForTeamCertification(teamCertification), [teamCertification]);

  const description =
    teamCertification.description ??
    getDefaultCertificationDescription(
      currentTeam?.name,
      certification.shortName,
      teamCertification.status,
      teamCertification.subtype
    );

  return (
    <Styles.CertificationCard>
      <div className="certification-card-logo">
        <img
          src={getCertificationLogo(certification.shortName, teamCertification.subtype)}
          srcSet={getCertificationLogoSrcSet(certification.shortName, teamCertification.subtype)}
          alt="Certification Logo"
        />
      </div>
      <Typography
        as="p"
        className="certification-card-title"
      >
        {getCertificationCardTitle(certification.shortName, teamCertification.subtype)}
      </Typography>
      <Typography
        as="p"
        level="8"
        strength="60%"
        className="certification-card-description"
        dangerouslySetInnerHTML={{ __html: description }}
      />
      {products?.length > 0 && (
        <Typography
          as="span"
          level="8"
          strength="60%"
          className="certification-card-related-products"
        >
          Applies to:
          <Typography
            as="span"
            level="8"
            strength="100%"
          >
            {products}
          </Typography>
        </Typography>
      )}
    </Styles.CertificationCard>
  );
}

export function Certifications() {
  const { currentTeam, authenticatedUser } = useAuthService();
  const [rawTeamCertifications] = useTeamCertificationList(currentTeam?.id);
  const sortedTeamCertifications = useMemo(() => sortTeamCertifications(rawTeamCertifications), []);

  const teamCertifications = getTeamCertificationsWithChildren(sortedTeamCertifications, authenticatedUser)?.filter(
    ({ children }) => children.length > 0
  );

  return (
    <>
      <Typography
        as="h1"
        level="1"
      >
        {PAGE_TITLE}
      </Typography>
      <Styles.Section className="certification-section-grid">
        <LeftPanel teamCertifications={teamCertifications} />
        <CertificationContentPanel teamCertifications={teamCertifications} />
      </Styles.Section>
    </>
  );
}

function EnterpriseCertificationListv2() {
  return (
    <>
      <Helmet pageTitle={PAGE_TITLE} />
      <Styles.Wrapper>
        <Certifications />
      </Styles.Wrapper>
    </>
  );
}

export const CertificationsWithNavigationBar = withTrustShareNavigationBar(() => (
  <Styles.CertificationContainer>
    <Certifications />
  </Styles.CertificationContainer>
));

export default EnterpriseCertificationListv2;
